import { useState } from "react"
import { useSmoothApi } from "src/hooks/useSmoothApi"
import { SMOOTH_LINE_API_URL } from "src/lib/constants"
import useSWR from "swr"
import type { DunningUserMemo } from "../types"

const memosUrl = (caseId: number) =>
  `${SMOOTH_LINE_API_URL}/base_api/smoother/screen/dunning_case_detail/${caseId}/memos`

export type UserMemoInput = {
  id?: number
  title: string
  body: string
}

/**
 * メモは1つのデータ群を2画面から扱う。
 * CSはチャット画面、督促担当はケース画面から同じユーザー単位の memos を編集する。
 */
export const useUserMemos = (caseId: number) => {
  const { getWithToken, postWithToken, patchWithToken, deleteWithToken } = useSmoothApi()
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const swr = useSWR(
    memosUrl(caseId),
    async (fetchUrl: string) => {
      const response = await getWithToken(fetchUrl)
      return (response.data as { data: DunningUserMemo[] }).data
    },
    // メモタブを開き直したときも取り直す。チャット画面側（CS）の編集を拾うため
    // （_app.tsx の既定 revalidateIfStale: false では前回の一覧が残る）
    { revalidateIfStale: true }
  )

  const saveMemo = async (input: UserMemoInput): Promise<boolean> => {
    setIsSaving(true)
    setSaveError(null)
    try {
      const payload = { title: input.title, body: input.body }
      if (input.id == null) {
        await postWithToken(memosUrl(caseId), payload)
      } else {
        await patchWithToken(`${memosUrl(caseId)}/${input.id}`, payload)
      }
      await swr.mutate()
      return true
    } catch (error) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        "メモの保存に失敗しました。"
      setSaveError(message)
      // 他画面（チャットページ）で先に更新・削除された場合の404等。グローバルSWR設定は
      // フォーカス時再検証を無効化しているため、失敗時も再取得して幽霊メモを残さない
      await swr.mutate()
      return false
    } finally {
      setIsSaving(false)
    }
  }

  const deleteMemo = async (memoId: number): Promise<boolean> => {
    setDeletingId(memoId)
    setDeleteError(null)
    try {
      await deleteWithToken(`${memosUrl(caseId)}/${memoId}`)
      await swr.mutate()
      return true
    } catch (error) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        "メモの削除に失敗しました。"
      setDeleteError(message)
      await swr.mutate()
      return false
    } finally {
      setDeletingId(null)
    }
  }

  return {
    memos: swr.data ?? [],
    isLoading: !swr.data && !swr.error,
    error: swr.error,
    saveMemo,
    isSaving,
    saveError,
    deleteMemo,
    deletingId,
    deleteError,
  }
}
